// push notifications via firebase cloud messaging (fcm)

const FIREBASE_VERSION = '9.0.0';
const STATUS_CHECK_INTERVAL_MINUTES = 30;
const STATUS_CACHE_DURATION_MS = 60 * 1000; // 1 minute

/**
 * @typedef {'unsupported'|'denied'|'not-initialized'|'subscribed'|'not-subscribed'|'token-invalid'|'error'} SubscriptionStatus
 */

export class PushNotificationManager {
    constructor() {
        this.firebaseConfig = window.FIREBASE_CONFIG ?? null;
        this.isSupported = 'serviceWorker' in navigator && 'Notification' in window;
        this.messaging = null;
        this._initPromise = null;
        this._intervalId = null;
        this._statusCache = null;
        this._statusCacheTime = 0;
        this._endpoints = {
            subscribe: '/api/push-subscriptions',
            status: '/api/push-subscriptions/status',
        };
        this._scripts = {
            app: `https://www.gstatic.com/firebasejs/${FIREBASE_VERSION}/firebase-app-compat.js`,
            messaging: `https://www.gstatic.com/firebasejs/${FIREBASE_VERSION}/firebase-messaging-compat.js`,
        };
    }

    getNotificationPermission() {
        if (typeof Notification === 'undefined' || !('Notification' in window)) {
            return 'denied';
        }
        return Notification.permission;
    }

    async init() {
        if (!this.isSupported || !this.firebaseConfig) return;
        if (this._initPromise) return this._initPromise;

        this._initPromise = (async () => {
            try {
                await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                await navigator.serviceWorker.ready;

                await this._ensureFirebaseLoaded();

                // Some browsers support Service Worker + Notification but not FCM
                if (typeof firebase.messaging?.isSupported === 'function' && !firebase.messaging.isSupported()) {
                    console.warn('[PushNotifications] Firebase Messaging not supported in this browser');
                    return;
                }

                if (!firebase.apps.length) {
                    firebase.initializeApp(this.firebaseConfig);
                }

                this.messaging = firebase.messaging();

                if (this.getNotificationPermission() === 'granted') {
                    await this.getTokenAndSubscribe();
                }

                this.startPeriodicCheck();
            } catch (error) {
                this._reportError('push_notification_init_failed', error);
            }
        })();

        return this._initPromise;
    }

    async _ensureFirebaseLoaded() {
        if (typeof firebase !== 'undefined') return;
        await this._loadScript(this._scripts.app);
        await this._loadScript(this._scripts.messaging);
    }

    _loadScript(src) {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`failed to load ${src}`));
            document.head.appendChild(script);
        });
    }

    async requestPermission() {
        if (!this.isSupported || !this.messaging) return false;

        try {
            if (this.getNotificationPermission() === 'granted') {
                return (await this.getTokenAndSubscribe()) === true;
            }

            const permission = typeof Notification !== 'undefined' ? await Notification.requestPermission() : 'denied';
            if (permission === 'granted') {
                return (await this.getTokenAndSubscribe()) === true;
            }
            return false;
        } catch (error) {
            this._reportError('push_notification_permission_failed', error);
            return false;
        }
    }

    async getTokenAndSubscribe() {
        if (!this.messaging) return false;
        try {
            const registration = await navigator.serviceWorker.ready;
            const vapidKey = this.firebaseConfig?.vapidKey || window.FIREBASE_VAPID_KEY || undefined;

            const token = await this.messaging.getToken({
                serviceWorkerRegistration: registration,
                ...(vapidKey ? { vapidKey } : {}),
            });

            if (token) {
                await this._sendTokenToServer(token);
                return true;
            }
            return false;
        } catch (error) {
            this._reportError('push_notification_token_failed', error);
            return false;
        }
    }

    async _sendTokenToServer(token) {
        try {
            const csrf = document.querySelector('meta[name="csrf-token"]');
            await fetch(this._endpoints.subscribe, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrf ? csrf.getAttribute('content') : '',
                    Accept: 'application/json',
                },
                body: JSON.stringify({
                    platform: 'web',
                    token,
                    device_model: navigator.userAgent,
                    app_version: window.APP_VERSION || '1.0.0',
                }),
            });
        } catch (error) {
            this._reportError('push_notification_subscribe_failed', error);
        }
    }

    async unsubscribe() {
        if (!this.messaging) return;
        try {
            const registration = await navigator.serviceWorker.ready;
            const vapidKey = this.firebaseConfig?.vapidKey || window.FIREBASE_VAPID_KEY || undefined;
            const token = await this.messaging.getToken({
                serviceWorkerRegistration: registration,
                ...(vapidKey ? { vapidKey } : {}),
            });
            if (!token) return;

            await this.messaging.deleteToken(token);

            const csrf = document.querySelector('meta[name="csrf-token"]');
            await fetch(this._endpoints.subscribe, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrf ? csrf.getAttribute('content') : '',
                    Accept: 'application/json',
                },
                body: JSON.stringify({ token }),
            });
        } catch (error) {
            this._reportError('push_notification_unsubscribe_failed', error);
        }
    }

    /**
     * Check the current subscription status
     * @returns {Promise<SubscriptionStatus>} The subscription status
     */
    async checkSubscriptionStatus() {
        if (!this.isSupported) return 'unsupported';
        if (this.getNotificationPermission() === 'denied') return 'denied';
        if (!this.messaging) return 'not-initialized';

        if (this.getNotificationPermission() === 'default') {
            try {
                const data = await this._fetchStatusCached();
                return data?.has_subscriptions ? 'subscribed' : 'not-subscribed';
            } catch (error) {
                this._reportError('push_notification_status_check_failed', error);
                return 'not-subscribed';
            }
        }

        try {
            const registration = await navigator.serviceWorker.ready;
            const vapidKey = this.firebaseConfig?.vapidKey || window.FIREBASE_VAPID_KEY || undefined;
            const token = await this.messaging.getToken({
                serviceWorkerRegistration: registration,
                ...(vapidKey ? { vapidKey } : {}),
            });
            const data = await this._fetchStatusCached();

            if (token && data && !data.has_subscriptions) return 'token-invalid';
            return token ? 'subscribed' : 'not-subscribed';
        } catch (error) {
            this._reportError('push_notification_status_check_failed', error);
            return 'error';
        }
    }

    async _fetchStatus() {
        try {
            const res = await fetch(this._endpoints.status, {
                method: 'GET',
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
            });
            if (!res.ok) return null;
            return res.json();
        } catch (error) {
            // network errors, user navigating away, etc.
            return null;
        }
    }

    /**
     * Fetch status with caching to reduce API calls
     * @private
     * @returns {Promise<Object|null>} The status data or null
     */
    async _fetchStatusCached() {
        const now = Date.now();
        if (this._statusCache && now - this._statusCacheTime < STATUS_CACHE_DURATION_MS) {
            return this._statusCache;
        }

        this._statusCache = await this._fetchStatus();
        this._statusCacheTime = now;
        return this._statusCache;
    }

    async checkAndPromptForResubscription() {
        const status = await this.checkSubscriptionStatus();

        if (this.getNotificationPermission() === 'default' && status === 'subscribed') {
            window.dispatchEvent(new CustomEvent('push-subscription-needed', { detail: { status: 'not-subscribed' } }));
            return false;
        }

        if (
            status === 'token-invalid' ||
            (status === 'not-subscribed' && this.getNotificationPermission() === 'default')
        ) {
            window.dispatchEvent(new CustomEvent('push-subscription-needed', { detail: { status } }));
            return false;
        }

        return status === 'subscribed';
    }

    /**
     * Start periodic subscription status checks
     * @param {number} intervalMinutes - Interval in minutes (default: 30)
     */
    startPeriodicCheck(intervalMinutes = STATUS_CHECK_INTERVAL_MINUTES) {
        this.checkAndPromptForResubscription();

        if (this._intervalId) return;
        this._intervalId = setInterval(
            () => {
                this.checkAndPromptForResubscription();
            },
            intervalMinutes * 60 * 1000
        );
    }

    /**
     * Stop periodic subscription status checks
     */
    stopPeriodicCheck() {
        if (this._intervalId) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
    }

    /**
     * Clean up resources and stop all background tasks
     */
    destroy() {
        this.stopPeriodicCheck();
        this._statusCache = null;
        this._statusCacheTime = 0;
    }

    /**
     * Register a callback for incoming messages
     * @param {Function} callback - Function to call when message received
     */
    onMessage(callback) {
        if (!this.messaging) return;
        this.messaging.onMessage(payload => callback(payload));
    }

    /**
     * Report errors to Sentry if available
     * @private
     * @param {string} context - Error context identifier
     * @param {Error} error - The error object
     */
    _reportError(context, error) {
        if (typeof Sentry !== 'undefined' && Sentry.captureException) {
            Sentry.captureException(error, {
                tags: { component: 'push_notifications', context },
                level: 'warning',
            });
        }
        console.warn(`[PushNotifications] ${context}:`, error);
    }
}

// export to window for global access
if (typeof window !== 'undefined') {
    window.PushNotificationManager = PushNotificationManager;
}
